import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbJsonLd, faqJsonLd } from '../../components/SEO';
import { STEP_VISUALS } from './stepVisuals';
import StepsScrolly from './StepsScrolly';
import SurfacesScrolly, { type FocusRegion } from './SurfacesScrolly';
import type { StepAnnotation } from './StepStage';
import { useScrollStory } from '../../hooks/useScrollStory';

const PAGE_URL = 'https://geofleet.ai/how-it-works';

const CDN = 'https://cdn.geofleet.in/images';

const IMG = {
  hero: `${CDN}/how-it-works-command-centre.png`,
  tower: `${CDN}/how-it-works:driver-tracking.png`,
  copilot: `${CDN}/how-it-works-copilot.png`,
} as const;

const TOWER_CARD_ICONS = ['ri-database-2-line', 'ri-route-line', 'ri-map-2-line', 'ri-file-shield-2-line'] as const;
const COPILOT_CARD_ICONS = ['ri-chat-3-line', 'ri-bubble-chart-line', 'ri-shield-check-line', 'ri-graduation-cap-line'] as const;

/**
 * Part of each screenshot a feature card points at, in card order, as fractions of the image.
 * Measured against the current CDN images; re-measure if a screenshot is replaced.
 */
const TOWER_CARD_FOCUS: readonly FocusRegion[] = [
  { x: 0.158, y: 0.878, w: 0.829, h: 0.108 }, // recent activity
  { x: 0.615, y: 0.541, w: 0.372, h: 0.335 }, // drivers table
  { x: 0.158, y: 0.262, w: 0.829, h: 0.28 }, // map and exceptions
  { x: 0.158, y: 0.15, w: 0.829, h: 0.112 }, // KPI row
];
const COPILOT_CARD_FOCUS: readonly FocusRegion[] = [
  { x: 0.585, y: 0.08, w: 0.406, h: 0.167 }, // the question
  { x: 0.62, y: 0.276, w: 0.368, h: 0.26 }, // answer table
  { x: 0.62, y: 0.542, w: 0.368, h: 0.3 }, // suggested reassignment
  { x: 0.585, y: 0.855, w: 0.408, h: 0.125 }, // prompt box and starters
];

type FeatureCard = { title: string; description: string };
type Step = {
  title: string;
  body: string;
  points: string[];
  linkLabel: string;
  linkHref: string;
  imageAlt: string;
};
type Faq = { question: string; answer: string };
type HeroFact = { icon: string; label: string };

function readArray<T>(t: TFunction, key: string, isItem: (o: Record<string, unknown>) => boolean): T[] {
  const raw = t(`howItWorksPage.${key}`, { returnObjects: true });
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is T => typeof item === 'object' && item !== null && isItem(item as Record<string, unknown>)
  );
}

const isStr = (v: unknown) => typeof v === 'string';

export default function HowItWorksPage() {
  const { t, i18n } = useTranslation();

  const heroFacts = useMemo(() => readArray<HeroFact>(t, 'heroFacts', (o) => isStr(o.icon) && isStr(o.label)), [t, i18n.language]);
  const steps = useMemo(
    () =>
      readArray<Step>(
        t,
        'steps',
        (o) => isStr(o.title) && isStr(o.body) && Array.isArray(o.points) && isStr(o.linkHref) && isStr(o.linkLabel)
      ),
    [t, i18n.language]
  );
  const scrollStory = useScrollStory();
  /** Status and detail chips per step — shared with the homepage scroll story. */
  const annotations = useMemo(() => {
    const raw = t('home.lifecycle.annotations', { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (o): o is StepAnnotation => typeof o === 'object' && o !== null && isStr((o as StepAnnotation).status) && isStr((o as StepAnnotation).detail)
    );
  }, [t, i18n.language]);
  const towerCards = useMemo(
    () => readArray<FeatureCard>(t, 'towerFeatureCards', (o) => isStr(o.title) && isStr(o.description)),
    [t, i18n.language]
  );
  const copilotCards = useMemo(
    () => readArray<FeatureCard>(t, 'copilotFeatureCards', (o) => isStr(o.title) && isStr(o.description)),
    [t, i18n.language]
  );
  const surfacePanels = useMemo(
    () => [
      {
        id: 'tower',
        title: t('howItWorksPage.towerSectionTitle'),
        lead: t('howItWorksPage.towerLead'),
        img: IMG.tower,
        alt: t('howItWorksPage.visualCommandAlt'),
        cards: towerCards,
        icons: TOWER_CARD_ICONS,
        focus: TOWER_CARD_FOCUS,
        chip: 'bg-emerald-100 text-emerald-800',
        chipActive: 'bg-emerald-600 text-white',
        spot: 'border-emerald-500',
        ring: 'border-emerald-100 ring-emerald-500/10',
        icon: 'ri-dashboard-3-line',
        tint: 'bg-gradient-to-br from-emerald-50/80 via-white to-white',
      },
      {
        id: 'copilot',
        title: t('howItWorksPage.copilotSectionTitle'),
        lead: t('howItWorksPage.copilotLead'),
        img: IMG.copilot,
        alt: t('howItWorksPage.visualCopilotAlt'),
        cards: copilotCards,
        icons: COPILOT_CARD_ICONS,
        focus: COPILOT_CARD_FOCUS,
        chip: 'bg-blue-100 text-blue-800',
        chipActive: 'bg-blue-600 text-white',
        spot: 'border-blue-500',
        ring: 'border-blue-100 ring-blue-500/10',
        icon: 'ri-sparkling-2-line',
        tint: 'bg-gradient-to-br from-blue-50/80 via-white to-white',
      },
    ],
    [t, towerCards, copilotCards]
  );
  const faqs = useMemo(() => readArray<Faq>(t, 'faqs', (o) => isStr(o.question) && isStr(o.answer)), [t, i18n.language]);

  const howToJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: t('howItWorksPage.stepsTitle'),
      description: t('howItWorksPage.heroLead'),
      step: steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.body,
        url: `${PAGE_URL}#step-${i + 1}`,
        image: new URL(STEP_VISUALS[i]?.img ?? IMG.hero, 'https://geofleet.ai').href,
      })),
    }),
    [t, steps]
  );

  return (
    <div className="pt-24">
      <SEO
        title={t('howItWorksPage.seoTitle')}
        description={t('howItWorksPage.seoDescription')}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'How It Works', url: PAGE_URL },
          ]),
          howToJsonLd,
          faqJsonLd(faqs),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white pb-12 pt-10 lg:pb-16 lg:pt-14">
        <div className="pointer-events-none absolute right-0 top-0 h-[min(380px,70vw)] w-[min(520px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full bg-blue-500/[0.07] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-[280px] w-[min(420px,85vw)] -translate-x-1/4 translate-y-1/4 rounded-full bg-emerald-500/[0.05] blur-3xl" />
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100/90 bg-white/90 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-sm">
            <i className="ri-flow-chart text-base" aria-hidden />
            {t('howItWorksPage.heroBadge')}
          </div>
          <h1 className="text-balance text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-[2.65rem] lg:leading-[1.12]">
            {t('howItWorksPage.heroTitle')}{' '}
            <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {t('howItWorksPage.heroTitleHighlight')}
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-gray-600">{t('howItWorksPage.heroLead')}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-500"
            >
              {t('common.ctaStartTrial')}
              <i className="ri-arrow-right-line text-base" aria-hidden />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-all hover:border-gray-400"
            >
              <i className="ri-calendar-line text-blue-600" aria-hidden />
              {t('common.ctaBookDemo')}
            </a>
          </div>
          {heroFacts.length > 0 && (
            <ul className="m-0 mt-8 flex list-none flex-wrap items-center justify-center gap-x-6 gap-y-2 p-0 text-sm text-gray-600">
              {heroFacts.map((fact) => (
                <li key={fact.label} className="inline-flex items-center gap-1.5">
                  <i className={`${fact.icon} text-emerald-600`} aria-hidden />
                  {fact.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="relative z-10 mx-auto mt-12 max-w-5xl px-6">
          <div className="overflow-hidden rounded-2xl border border-gray-200/90 bg-gradient-to-br from-gray-50/80 to-white shadow-xl shadow-gray-200/50 ring-1 ring-black/[0.04]">
            <img
              src={IMG.hero}
              alt={t('howItWorksPage.visualHeroAlt')}
              className="h-auto w-full object-contain p-4 sm:p-6"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* Step jump nav + lifecycle steps */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-16 lg:py-24" aria-labelledby="hiw-steps-title">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('howItWorksPage.stepsKicker')}</p>
            <h2 id="hiw-steps-title" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {t('howItWorksPage.stepsTitle')}
            </h2>
            <p className="mt-4 leading-relaxed text-gray-600">{t('howItWorksPage.stepsIntro')}</p>
          </div>

          <nav aria-label={t('howItWorksPage.stepsTitle')} className="mb-14 lg:mb-20">
            <ol className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3 lg:grid-cols-6">
              {steps.map((step, i) => (
                <li key={step.title}>
                  <a
                    href={`#step-${i + 1}`}
                    className="flex h-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-700"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[11px] font-bold text-white tabular-nums">
                      {i + 1}
                    </span>
                    <span className="leading-tight">{step.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {scrollStory ? (
            <StepsScrolly
              steps={steps}
              annotations={annotations}
              stepLabel={(n) => t('howItWorksPage.stepLabel', { n })}
            />
          ) : (
          <ol className="m-0 list-none space-y-20 p-0 lg:space-y-28">
            {steps.map((step, i) => {
              const visual = STEP_VISUALS[i];
              const flip = i % 2 === 1;
              return (
                <li key={step.title} id={`step-${i + 1}`} className="scroll-mt-28">
                  <article className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
                    <div className={flip ? 'lg:order-2' : ''}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                          <i className={`${visual?.icon ?? 'ri-checkbox-circle-line'} text-xl`} aria-hidden />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                          {t('howItWorksPage.stepLabel', { n: i + 1 })}
                        </span>
                      </div>
                      <h3 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">{step.title}</h3>
                      <p className="mt-4 text-[15px] leading-relaxed text-gray-600 sm:text-base">{step.body}</p>
                      <ul className="m-0 mt-5 list-none space-y-2 p-0">
                        {step.points.map((point) => (
                          <li key={point} className="flex items-start gap-2 text-sm text-gray-700">
                            <i className="ri-check-line mt-0.5 text-base text-emerald-600" aria-hidden />
                            {point}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={step.linkHref}
                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600"
                      >
                        {step.linkLabel}
                        <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                      </Link>
                    </div>
                    {visual && (
                      <div className={flip ? 'lg:order-1' : ''}>
                        <div className="flex items-center justify-center overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-3 shadow-lg shadow-gray-200/60 ring-1 ring-black/[0.03] sm:p-4">
                          <img
                            src={visual.img}
                            alt={step.imageAlt}
                            loading="lazy"
                            className="h-auto max-h-[420px] w-full rounded-xl object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </article>
                </li>
              );
            })}
          </ol>
          )}
        </div>
      </section>

      {/* Command centre + Copilot */}
      <section
        id="command-centre"
        className={`relative scroll-mt-24 border-t border-gray-100 bg-white ${scrollStory ? '' : 'py-16'}`}
        aria-labelledby="hiw-surfaces-heading"
      >
        {scrollStory ? (
          <SurfacesScrolly
            panels={surfacePanels}
            kicker={t('howItWorksPage.surfacesKicker')}
            title={t('howItWorksPage.surfacesTitle')}
            intro={t('howItWorksPage.surfacesIntro')}
          />
        ) : (
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">{t('howItWorksPage.surfacesKicker')}</p>
            <h2 id="hiw-surfaces-heading" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {t('howItWorksPage.surfacesTitle')}
            </h2>
            <p className="mt-4 leading-relaxed text-gray-600">{t('howItWorksPage.surfacesIntro')}</p>
          </div>

          <div className="flex flex-col gap-8 lg:gap-10">
            {surfacePanels.map((panel, idx) => (
              <article
                key={panel.id}
                id={panel.id === 'copilot' ? 'copilot' : undefined}
                className={`scroll-mt-24 grid overflow-hidden rounded-3xl border bg-gradient-to-b from-slate-50/80 to-white shadow-sm ring-1 lg:grid-cols-[7fr_5fr] lg:items-center ${panel.ring}`}
              >
                <div
                  className={`border-b border-gray-100 bg-white p-4 lg:h-full lg:border-b-0 lg:flex lg:items-center ${
                    idx % 2 ? 'lg:order-2 lg:border-l' : 'lg:border-r'
                  }`}
                >
                  <img src={panel.img} alt={panel.alt} loading="lazy" className="h-auto w-full rounded-xl object-contain" />
                </div>
                <div className="p-6 sm:p-8 lg:p-10">
                  <h3 className="text-2xl font-bold text-gray-900">{panel.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-gray-600">{panel.lead}</p>
                  <ul className="m-0 mt-6 grid list-none gap-5 p-0 sm:grid-cols-2">
                    {panel.cards.map((card, i) => (
                      <li key={card.title} className="flex gap-3">
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${panel.chip}`}>
                          <i className={`${panel.icons[i] ?? 'ri-layout-grid-line'} text-lg`} aria-hidden />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{card.title}</h4>
                          <p className="mt-1 text-sm leading-relaxed text-gray-600">{card.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
        )}
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="border-t border-gray-100 bg-slate-50/70 py-16 lg:py-24" aria-labelledby="hiw-faq-heading">
          <div className="mx-auto max-w-3xl px-6">
            <div className="mb-10 text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('howItWorksPage.faqKicker')}</p>
              <h2 id="hiw-faq-heading" className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
                {t('howItWorksPage.faqTitle')}
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details
                  key={faq.question}
                  open={i === 0}
                  className="group rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm open:shadow-md"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
                    <h3 className="text-base">{faq.question}</h3>
                    <i className="ri-add-line shrink-0 text-xl text-gray-400 transition-transform group-open:rotate-45" aria-hidden />
                  </summary>
                  <p className="mt-3 text-[15px] leading-relaxed text-gray-600">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-gray-800 bg-gray-950 py-16 lg:py-22">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-950/80 via-gray-950 to-indigo-950/40" />
        <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" aria-hidden />
        <div className="relative z-10 mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-2xl font-bold text-white lg:text-3xl">{t('howItWorksPage.closingTitle')}</h2>
          <p className="mt-4 text-gray-300 leading-relaxed">{t('howItWorksPage.closingSub')}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/35 transition-all hover:bg-blue-500"
            >
              {t('common.ctaStartTrial')}
              <i className="ri-arrow-right-line text-base" aria-hidden />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl border-2 border-emerald-400/90 bg-transparent px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-emerald-500/15"
            >
              <i className="ri-calendar-line text-emerald-400" aria-hidden />
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
